import { useCallback, useEffect, useRef, useState } from "react";
import "@/App.css";
import axios from "axios";
import Lenis from "lenis";
import { AnimatePresence, motion } from "framer-motion";
import { Loader2, RefreshCw } from "lucide-react";
import { Toaster, toast } from "@/components/ui/sonner";
import Navbar from "@/components/Navbar";
import Hero from "@/components/Hero";
import CommunityBanner from "@/components/CommunityBanner";
import Marquee from "@/components/Marquee";
import TournamentCard from "@/components/TournamentCard";
import RegisterModal from "@/components/RegisterModal";
import SuccessOverlay from "@/components/SuccessOverlay";
import Faq from "@/components/Faq";
import Footer from "@/components/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function App() {
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [success, setSuccess] = useState(null);
  const lenisRef = useRef(null);

  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09, smoothWheel: true });
    lenisRef.current = lenis;
    let raf;
    const loop = (t) => {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
    };
  }, []);

  const fetchTournaments = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/tournaments`, {
        params: { t: Date.now() },
      });
      setTournaments(data.tournaments || []);
      setError(null);
    } catch (e) {
      setError("The tournament feed is unreachable right now.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTournaments();
  }, [fetchTournaments]);

  const scrollTo = useCallback((id) => {
    if (lenisRef.current) {
      lenisRef.current.scrollTo(`#${id}`, { offset: -72, duration: 1.4 });
    }
  }, []);

  const openCount = tournaments.filter(
    (t) => t.status !== "confirmed" && t.registeredTeams < t.capacity,
  ).length;

  const handleRegistered = (info) => {
    setSelected(null);
    setSuccess(info);
    fetchTournaments();
  };

  return (
    <div className="App min-h-screen bg-[#08040F] text-slate-100 antialiased">
      <Toaster position="top-center" theme="dark" />
      <Navbar onNavigate={scrollTo} />

      <main>
        <Hero onViewTournaments={() => scrollTo("tournaments")} openCount={openCount} />
        <CommunityBanner />
        <Marquee />

        <section id="tournaments" className="px-4 py-16 sm:px-8 sm:py-24 lg:px-16 max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="mb-10 sm:mb-14"
          >
            <p className="font-mono-b3b text-xs tracking-[0.3em] text-yellow-400 uppercase mb-3">
              01 / The Bracket
            </p>
            <h2 className="font-display text-4xl sm:text-6xl uppercase leading-none tracking-wide">
              Pick your <span className="text-yellow-400">battle</span>
            </h2>
            <p className="mt-3 text-sm sm:text-base text-slate-400 max-w-xl">
              Get points for every tournament. Run the Leaderboard.
            </p>
          </motion.div>

          {loading && (
            <div className="flex items-center justify-center py-24" data-testid="tournaments-loading">
              <Loader2 className="h-8 w-8 animate-spin text-purple-400" />
            </div>
          )}

          {!loading && error && (
            <div className="rounded-2xl border border-purple-500/20 bg-[#130A24] p-10 text-center" data-testid="tournaments-error">
              <p className="text-slate-300 mb-6">{error}</p>
              <button
                data-testid="tournaments-retry-btn"
                onClick={() => {
                  setLoading(true);
                  fetchTournaments();
                }}
                className="inline-flex items-center gap-2 rounded-full bg-yellow-400 px-6 py-3 font-heading font-bold text-black uppercase tracking-wide text-sm transition-transform hover:scale-105"
              >
                <RefreshCw className="h-4 w-4" /> Retry
              </button>
            </div>
          )}

          {!loading && !error && tournaments.length === 0 && (
            <div className="rounded-2xl border border-purple-500/20 bg-[#130A24] p-10 text-center" data-testid="tournaments-empty">
              <p className="text-slate-300">No tournaments scheduled yet — check back soon.</p>
            </div>
          )}

          {!loading && !error && tournaments.length > 0 && (
            <motion.div
              data-testid="tournament-grid"
              className="grid grid-cols-1 gap-6 sm:gap-8 md:grid-cols-2 lg:grid-cols-3"
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, margin: "-60px" }}
              variants={{ hidden: {}, show: { transition: { staggerChildren: 0.08 } } }}
            >
              {tournaments.map((t, i) => (
                <TournamentCard key={t.id} tournament={t} index={i} onRegister={() => setSelected(t)} />
              ))}
            </motion.div>
          )}
        </section>

        <Faq />
      </main>

      <Footer />

      <AnimatePresence>
        {selected && (
          <RegisterModal
            tournament={selected}
            onClose={() => setSelected(null)}
            onSuccess={handleRegistered}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {success && (
          <SuccessOverlay
            info={success}
            onDone={() => {
              setSuccess(null);
              toast.success("You're on the bracket. See you on court.");
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
